import { useState, useEffect, useRef, useCallback, memo } from 'react'
import {
  MessageCircle,
  Send,
  User,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Phone,
  Mail,
  Car,
  MoreVertical,
  X,
} from 'lucide-react'
import { api } from '../../services/api'
import { useNotification } from '../../contexts/NotificationContext'
import { PageHeader } from '../../components/ui'
import { card, Badge, Button, buttonClass, type BadgeTone } from '../../design-system'

// ==================== INTERFACES ====================

interface ChatDashboard {
  chatsAguardando: number
  chatsAtivos: number
  chatsHoje: number
  mensagensNaoLidas: number
  tempoMedioRespostaMinutos: number | null
  chatsRecentes: ChatSessaoLista[]
}

interface ChatSessaoLista {
  sessaoId: string
  visitanteNome: string | null
  status: 'AGUARDANDO' | 'ATIVA' | 'ENCERRADA'
  atendenteNome: string | null
  mensagensNaoLidas: number
  ultimaMensagemTexto: string | null
  ultimaMensagem: string | null
  veiculoId: number | null
  veiculoNome: string | null
}

interface ChatSessao {
  id: number
  sessaoId: string
  lojaId: number
  visitanteNome: string | null
  visitanteEmail: string | null
  visitanteTelefone: string | null
  veiculoId: number | null
  veiculoNome: string | null
  status: 'AGUARDANDO' | 'ATIVA' | 'ENCERRADA'
  atendenteId: number | null
  atendenteNome: string | null
  totalMensagens: number
  mensagensNaoLidas: number
  ultimaMensagem: string | null
  dataInicio: string
  dataEncerramento: string | null
}

interface ChatMensagem {
  id: number
  sessaoId: string
  remetente: 'VISITANTE' | 'ATENDENTE' | 'SISTEMA'
  mensagem: string
  tipo: string
  atendenteNome: string | null
  veiculoId: number | null
  veiculoNome: string | null
  lida: boolean
  dataEnvio: string
}

interface Atendente {
  id: number
  nome: string
}

// ==================== COMPONENTES ====================

const StatusBadge = memo(({ status }: { status: string }) => {
  const styles: Record<string, { tone: BadgeTone; label: string }> = {
    AGUARDANDO: { tone: 'warning', label: 'Aguardando' },
    ATIVA: { tone: 'success', label: 'Ativa' },
    ENCERRADA: { tone: 'neutral', label: 'Encerrada' },
  }
  const style = styles[status] || styles.AGUARDANDO

  return <Badge tone={style.tone}>{style.label}</Badge>
})
StatusBadge.displayName = 'StatusBadge'

const MetricCard = memo(({ icon, value, label }: {
  icon: React.ReactNode
  value: number | string
  label: string
}) => (
  <div className={`${card} flex items-center gap-4 p-4`}>
    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-slate-50 text-slate-400 ring-1 ring-slate-100">
      {icon}
    </div>
    <div>
      <p className="text-2xl font-bold tracking-tight tabular-nums text-slate-900">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  </div>
))
MetricCard.displayName = 'MetricCard'

// ==================== PÁGINA PRINCIPAL ====================

export default function ChatAtendimento() {
  const { showApiError } = useNotification()

  // Dashboard state
  const [dashboard, setDashboard] = useState<ChatDashboard | null>(null)
  const [loadingDashboard, setLoadingDashboard] = useState(true)

  // Sessions list state
  const [sessoes, setSessoes] = useState<ChatSessaoLista[]>([])
  const [filtroStatus, setFiltroStatus] = useState<string>('AGUARDANDO')

  // Selected chat state
  const [sessaoSelecionada, setSessaoSelecionada] = useState<ChatSessao | null>(null)
  const [mensagens, setMensagens] = useState<ChatMensagem[]>([])
  const [novaMensagem, setNovaMensagem] = useState('')
  const [loadingMensagens, setLoadingMensagens] = useState(false)
  const [enviando, setEnviando] = useState(false)

  // Atendentes
  const [atendentes, setAtendentes] = useState<Atendente[]>([])
  const [showAtendentesModal, setShowAtendentesModal] = useState(false)

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastMessageIdRef = useRef<number | null>(null)

  // ==================== FUNCS ====================

  const loadDashboard = useCallback(async () => {
    try {
      const response = await api.get<ChatDashboard>('/chat/dashboard')
      setDashboard(response.data)
      // Não sobrescrever sessões aqui - deixar loadSessoes gerenciar a lista
    } catch (error) {
      showApiError(error)
    } finally {
      setLoadingDashboard(false)
    }
  }, [showApiError])

  const loadSessoes = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (filtroStatus) {
        // Enviar todos os status separados por vírgula
        params.append('status', filtroStatus)
      }
      const response = await api.get(`/chat/sessoes?${params.toString()}`)
      setSessoes(response.data.sessoes || response.data.chatsRecentes || [])
    } catch (error) {
      showApiError(error)
    }
  }, [filtroStatus, showApiError])

  const loadAtendentes = useCallback(async () => {
    try {
      const response = await api.get<Atendente[]>('/chat/atendentes')
      setAtendentes(response.data)
    } catch (error) {
      console.error('Erro ao carregar atendentes:', error)
    }
  }, [])

  const loadSessaoDetalhes = useCallback(async (sessaoId: string) => {
    setLoadingMensagens(true)
    try {
      const [sessaoRes, mensagensRes] = await Promise.all([
        api.get<ChatSessao>(`/chat/sessoes/${sessaoId}`),
        api.get<ChatMensagem[]>(`/chat/sessoes/${sessaoId}/mensagens`)
      ])
      setSessaoSelecionada(sessaoRes.data)
      setMensagens(mensagensRes.data)
      if (mensagensRes.data.length > 0) {
        lastMessageIdRef.current = mensagensRes.data[mensagensRes.data.length - 1].id
      }
      // Mark as read
      await api.post(`/chat/sessoes/${sessaoId}/marcar-lida`)
    } catch (error) {
      showApiError(error)
    } finally {
      setLoadingMensagens(false)
    }
  }, [showApiError])

  const pollMensagens = useCallback(async () => {
    if (!sessaoSelecionada) return
    try {
      const params = lastMessageIdRef.current ? `?aposId=${lastMessageIdRef.current}` : ''
      const response = await api.get<ChatMensagem[]>(`/chat/sessoes/${sessaoSelecionada.sessaoId}/mensagens${params}`)
      if (response.data.length > 0) {
        setMensagens(prev => [...prev, ...response.data])
        lastMessageIdRef.current = response.data[response.data.length - 1].id
      }
    } catch (error) {
      console.error('Erro ao buscar novas mensagens:', error)
    }
  }, [sessaoSelecionada])

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const handleEnviarMensagem = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!novaMensagem.trim() || !sessaoSelecionada || enviando) return

    const mensagemTexto = novaMensagem.trim()
    setNovaMensagem('')
    setEnviando(true)

    try {
      await api.post(`/chat/sessoes/${sessaoSelecionada.sessaoId}/mensagens`, {
        sessaoId: sessaoSelecionada.sessaoId,
        mensagem: mensagemTexto,
        tipo: 'TEXTO'
      })
      // Reload messages
      await pollMensagens()
    } catch (error) {
      showApiError(error)
      setNovaMensagem(mensagemTexto) // Restore message on error
    } finally {
      setEnviando(false)
    }
  }

  const handleAtribuir = async (atendenteId?: number) => {
    if (!sessaoSelecionada) return
    try {
      await api.post(`/chat/sessoes/${sessaoSelecionada.sessaoId}/atribuir`, {
        sessaoId: sessaoSelecionada.sessaoId,
        atendenteId: atendenteId || 0 // 0 = self
      })
      setShowAtendentesModal(false)
      loadSessaoDetalhes(sessaoSelecionada.sessaoId)
      loadSessoes()
    } catch (error) {
      showApiError(error)
    }
  }

  const handleEncerrar = async () => {
    if (!sessaoSelecionada) return
    if (!confirm('Deseja encerrar este chat?')) return
    try {
      await api.post(`/chat/sessoes/${sessaoSelecionada.sessaoId}/encerrar`, {
        sessaoId: sessaoSelecionada.sessaoId
      })
      setSessaoSelecionada(null)
      setMensagens([])
      loadDashboard()
      loadSessoes()
    } catch (error) {
      showApiError(error)
    }
  }

  // ==================== EFFECTS ====================

  useEffect(() => {
    loadDashboard()
    loadAtendentes()
    loadSessoes() // Carregar sessões inicialmente
  }, []) // Executar apenas na montagem

  useEffect(() => {
    loadSessoes()
  }, [filtroStatus]) // Recarregar quando filtro mudar

  useEffect(() => {
    scrollToBottom()
  }, [mensagens, scrollToBottom])

  // Polling for new messages
  useEffect(() => {
    if (sessaoSelecionada && sessaoSelecionada.status !== 'ENCERRADA') {
      pollingRef.current = setInterval(pollMensagens, 3000)
      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
        }
      }
    }
  }, [sessaoSelecionada, pollMensagens])

  // ==================== RENDER ====================

  const formatTime = (dateString: string | null) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      <PageHeader
        title="Chat ao Vivo"
        subtitle="Atendimento em tempo real aos visitantes do site"
      >
        <Button
          variant="secondary"
          onClick={() => { loadDashboard(); loadSessoes() }}
        >
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </Button>
      </PageHeader>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4 px-6 py-4 md:grid-cols-4">
        <MetricCard
          icon={<Clock className="h-5 w-5" />}
          value={dashboard?.chatsAguardando ?? 0}
          label="Aguardando"
        />
        <MetricCard
          icon={<MessageCircle className="h-5 w-5" />}
          value={dashboard?.chatsAtivos ?? 0}
          label="Ativos"
        />
        <MetricCard
          icon={<Mail className="h-5 w-5" />}
          value={dashboard?.mensagensNaoLidas ?? 0}
          label="Não lidas"
        />
        <MetricCard
          icon={<Clock className="h-5 w-5" />}
          value={dashboard?.tempoMedioRespostaMinutos ? `${dashboard.tempoMedioRespostaMinutos}min` : '-'}
          label="Tempo médio"
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden px-6 pb-4 gap-4">
        {/* Sessions List */}
        <div className={`${card} flex w-80 flex-col overflow-hidden`}>
          {/* Filter Tabs */}
          <div className="flex border-b border-slate-100">
            {[
              { value: 'AGUARDANDO', label: 'Pendentes' },
              { value: 'ATIVA', label: 'Em Atend.' },
              { value: 'ENCERRADA', label: 'Encerrados' },
            ].map(tab => (
              <button
                key={tab.value}
                onClick={() => setFiltroStatus(tab.value)}
                className={`flex-1 px-3 py-2.5 text-sm font-semibold transition-colors ${
                  filtroStatus === tab.value
                    ? 'border-b-2 border-primary-600 bg-primary-50 text-primary-600'
                    : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Sessions */}
          <div className="flex-1 divide-y divide-slate-100 overflow-y-auto">
            {loadingDashboard ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            ) : sessoes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                <MessageCircle className="mb-2 h-12 w-12 text-slate-300" />
                <p className="text-sm">Nenhum chat encontrado</p>
              </div>
            ) : (
              sessoes.map(sessao => (
                <button
                  key={sessao.sessaoId}
                  onClick={() => loadSessaoDetalhes(sessao.sessaoId)}
                  className={`w-full p-3 text-left transition-colors ${
                    sessaoSelecionada?.sessaoId === sessao.sessaoId
                      ? 'bg-primary-50'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="truncate font-semibold text-slate-900">
                          {sessao.visitanteNome || 'Visitante'}
                        </span>
                        {sessao.mensagensNaoLidas > 0 && (
                          <Badge tone="primary" className="shrink-0">
                            {sessao.mensagensNaoLidas}
                          </Badge>
                        )}
                      </div>
                      {sessao.veiculoNome && (
                        <p className="mb-1 truncate text-xs text-primary-600">
                          <Car className="mr-1 inline h-3 w-3" />
                          {sessao.veiculoNome}
                        </p>
                      )}
                      <p className="truncate text-sm text-slate-500">
                        {sessao.ultimaMensagemTexto || 'Aguardando mensagem...'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <StatusBadge status={sessao.status} />
                      <span className="text-xs text-slate-400">
                        {formatTime(sessao.ultimaMensagem)}
                      </span>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`${card} flex flex-1 flex-col overflow-hidden`}>
          {sessaoSelecionada ? (
            <>
              {/* Chat Header */}
              <div className="flex items-center justify-between border-b border-slate-100 p-4">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-400">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-bold tracking-tight text-slate-900">
                      {sessaoSelecionada.visitanteNome || 'Visitante'}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      {sessaoSelecionada.visitanteTelefone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {sessaoSelecionada.visitanteTelefone}
                        </span>
                      )}
                      {sessaoSelecionada.visitanteEmail && (
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {sessaoSelecionada.visitanteEmail}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={sessaoSelecionada.status} />
                  {sessaoSelecionada.status !== 'ENCERRADA' && (
                    <div className="relative">
                      <button
                        onClick={() => setShowAtendentesModal(!showAtendentesModal)}
                        className="grid h-9 w-9 place-items-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100"
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>
                      {showAtendentesModal && (
                        <div className={`absolute right-0 z-10 mt-2 w-48 ${card}`}>
                          <div className="py-1">
                            {!sessaoSelecionada.atendenteId && (
                              <button
                                onClick={() => handleAtribuir()}
                                className="w-full px-4 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                              >
                                <CheckCircle2 className="mr-2 inline h-4 w-4" />
                                Assumir Chat
                              </button>
                            )}
                            {atendentes.length > 0 && (
                              <>
                                <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
                                  Transferir para:
                                </div>
                                {atendentes.map(a => (
                                  <button
                                    key={a.id}
                                    onClick={() => handleAtribuir(a.id)}
                                    className="w-full px-4 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                                  >
                                    {a.nome}
                                  </button>
                                ))}
                              </>
                            )}
                            <button
                              onClick={handleEncerrar}
                              className="w-full border-t border-slate-100 px-4 py-2 text-left text-sm text-rose-600 transition-colors hover:bg-rose-50"
                            >
                              <XCircle className="mr-2 inline h-4 w-4" />
                              Encerrar Chat
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    onClick={() => { setSessaoSelecionada(null); setMensagens([]) }}
                    className="grid h-9 w-9 place-items-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Vehicle info */}
              {sessaoSelecionada.veiculoNome && (
                <div className="flex items-center gap-2 border-b border-slate-100 bg-primary-50 px-4 py-2">
                  <Car className="h-4 w-4 text-primary-600" />
                  <span className="text-sm text-primary-700">
                    Interessado em: <strong>{sessaoSelecionada.veiculoNome}</strong>
                  </span>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4">
                {loadingMensagens ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                  </div>
                ) : (
                  <>
                    <div className="py-2 text-center text-xs text-slate-400">
                      Chat iniciado em {formatDate(sessaoSelecionada.dataInicio)}
                    </div>
                    {mensagens.map(msg => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.remetente === 'ATENDENTE' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl p-3 ${
                            msg.remetente === 'ATENDENTE'
                              ? 'bg-primary-600 text-white'
                              : msg.remetente === 'SISTEMA'
                              ? 'bg-slate-200 text-sm italic text-slate-600'
                              : 'bg-slate-100 text-slate-800'
                          }`}
                        >
                          {msg.remetente === 'ATENDENTE' && msg.atendenteNome && (
                            <div className="mb-1 text-xs text-primary-100">{msg.atendenteNome}</div>
                          )}
                          <p className="whitespace-pre-wrap break-words">{msg.mensagem}</p>
                          <span
                            className={`mt-1 block text-xs ${
                              msg.remetente === 'ATENDENTE' ? 'text-primary-100' : 'text-slate-400'
                            }`}
                          >
                            {formatTime(msg.dataEnvio)}
                          </span>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input */}
              {sessaoSelecionada.status !== 'ENCERRADA' && (
                <form onSubmit={handleEnviarMensagem} className="border-t border-slate-100 bg-white p-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={novaMensagem}
                      onChange={(e) => setNovaMensagem(e.target.value)}
                      placeholder="Digite sua mensagem..."
                      className="h-10 flex-1 rounded-xl border border-slate-200 px-4 text-sm transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                      disabled={enviando}
                    />
                    <button
                      type="submit"
                      disabled={enviando || !novaMensagem.trim()}
                      className={buttonClass('primary', 'icon')}
                    >
                      {enviando ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Send className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </form>
              )}

              {sessaoSelecionada.status === 'ENCERRADA' && (
                <div className="border-t border-slate-100 bg-slate-50 p-4 text-center text-slate-500">
                  Este chat foi encerrado em {formatDate(sessaoSelecionada.dataEncerramento)}
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center text-slate-400">
              <MessageCircle className="mb-4 h-16 w-16 text-slate-300" />
              <p className="text-lg">Selecione um chat para iniciar o atendimento</p>
              <p className="text-sm">Os chats aguardando atendimento aparecem na lista à esquerda</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
